import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Text, HStack, VStack, Flex, Image, Tooltip } from '@chakra-ui/react';
import { MdDragIndicator } from 'react-icons/md';
import UploadSlotPlaceholderIcon from './UploadSlotPlaceholderIcon';
import { ff } from '../../../../../../theme';
import { reorderTopSlots } from '../../lib/marketplaceProfileAssets';
import type { TopContentSlotEntry } from '../../types';

const MIN_PORTFOLIO = 3;
const MAX_PORTFOLIO = 5;
const DRAG_CLICK_THRESHOLD_PX = 8;

export interface TopVideosSectionProps {
  topSlots: (TopContentSlotEntry | null)[];
  filledCount: number;
  onSlotClick: (slotIndex: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

type DragGhostMetrics = {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
};

function renderSlotMedia(src: TopContentSlotEntry) {
  if (src.isVideo) {
    return (
      <Box
        as="video"
        src={src.url}
        muted
        loop
        playsInline
        autoPlay
        objectFit="cover"
        w="100%"
        h="100%"
        position="absolute"
        inset={0}
        pointerEvents="none"
      />
    );
  }
  return (
    <Image
      src={src.url}
      alt=""
      objectFit="cover"
      w="100%"
      h="100%"
      position="absolute"
      inset={0}
      pointerEvents="none"
    />
  );
}

/**
 * Top Content / portfolio: 3–5 slots, click a slot to upload. Fourth and fifth
 * appear only when all prior slots in the current row are filled.
 */
const TopVideosSection: React.FC<TopVideosSectionProps> = ({
  topSlots,
  filledCount,
  onSlotClick,
  onReorder,
}) => {
  const [coverHintOpen, setCoverHintOpen] = useState(false);
  const [previewSlots, setPreviewSlots] = useState<(TopContentSlotEntry | null)[] | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{ left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragSessionActive, setDragSessionActive] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [ghostMetrics, setGhostMetrics] = useState<DragGhostMetrics | null>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const didDragRef = useRef(false);
  const dragEndedRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragSourceIndexRef = useRef<number | null>(null);
  const canReorder = filledCount >= 2;
  const displaySlots = previewSlots ?? topSlots;

  const draggedEntry =
    draggedId !== null ? topSlots.find((s) => s?.id === draggedId) ?? null : null;

  const findSlotIndexAtClientX = useCallback((clientX: number): number | null => {
    for (let i = 0; i < slotRefs.current.length; i++) {
      const el = slotRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return i;
      }
    }
    return null;
  }, []);

  const resetDrag = useCallback(() => {
    setPreviewSlots(null);
    setGhostPosition(null);
    setIsDragging(false);
    setDragSessionActive(false);
    setDraggedId(null);
    dragStartRef.current = null;
    setGhostMetrics(null);
    dragSourceIndexRef.current = null;
    didDragRef.current = false;
    dragEndedRef.current = false;
  }, []);

  const updateGhostPosition = useCallback(
    (clientX: number, clientY: number, metrics: DragGhostMetrics) => {
      setGhostPosition({
        left: clientX - metrics.offsetX,
        top: clientY - metrics.offsetY,
      });
    },
    []
  );

  const handleDragHandlePointerDown = useCallback(
    (index: number, e: React.PointerEvent<HTMLDivElement>) => {
      if (!canReorder || !topSlots[index]) return;
      e.preventDefault();
      e.stopPropagation();

      const slotEl = slotRefs.current[index];
      if (!slotEl) return;

      didDragRef.current = false;
      dragEndedRef.current = false;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      dragSourceIndexRef.current = index;
      setDraggedId(topSlots[index]!.id);
      setDragSessionActive(true);

      const rect = slotEl.getBoundingClientRect();
      const metrics: DragGhostMetrics = {
        width: rect.width,
        height: rect.height,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
      };
      setGhostMetrics(metrics);
      setPreviewSlots(null);
      setGhostPosition({
        left: e.clientX - metrics.offsetX,
        top: e.clientY - metrics.offsetY,
      });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [canReorder, topSlots]
  );

  const applyPreviewAtTarget = useCallback(
    (targetIndex: number, activeDraggedId: string) => {
      setPreviewSlots((prev) => {
        const base = prev ?? topSlots;
        const fromIndex = base.findIndex((s) => s?.id === activeDraggedId);
        if (fromIndex < 0 || fromIndex === targetIndex) {
          return prev ?? base;
        }
        return reorderTopSlots(base, fromIndex, targetIndex);
      });
    },
    [topSlots]
  );

  const finishDrag = useCallback(
    (commit: boolean) => {
      if (dragEndedRef.current) return;
      dragEndedRef.current = true;

      const fromIndex = dragSourceIndexRef.current;
      if (commit && didDragRef.current && fromIndex !== null && draggedId !== null) {
        const slots = previewSlots ?? topSlots;
        const toIndex = slots.findIndex((s) => s?.id === draggedId);
        if (toIndex >= 0 && toIndex !== fromIndex) {
          onReorder(fromIndex, toIndex);
        }
      }
      resetDrag();
    },
    [draggedId, onReorder, previewSlots, resetDrag, topSlots]
  );

  const handlePointerMove = useCallback(
    (clientX: number, clientY: number) => {
      if (dragSourceIndexRef.current === null || !dragStartRef.current) return;

      const dx = clientX - dragStartRef.current.x;
      const dy = clientY - dragStartRef.current.y;
      if (!didDragRef.current && Math.hypot(dx, dy) >= DRAG_CLICK_THRESHOLD_PX) {
        didDragRef.current = true;
        setIsDragging(true);
      }

      if (!didDragRef.current) return;

      if (ghostMetrics) {
        updateGhostPosition(clientX, clientY, ghostMetrics);
      }

      const target = findSlotIndexAtClientX(clientX);
      if (target !== null && draggedId !== null) {
        applyPreviewAtTarget(target, draggedId);
      }
    },
    [applyPreviewAtTarget, draggedId, findSlotIndexAtClientX, ghostMetrics, updateGhostPosition]
  );

  useEffect(() => {
    if (!dragSessionActive) return;

    const onWindowPointerMove = (e: PointerEvent) => {
      handlePointerMove(e.clientX, e.clientY);
    };
    const onWindowPointerUp = () => {
      finishDrag(didDragRef.current);
    };

    window.addEventListener('pointermove', onWindowPointerMove);
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerUp);

    return () => {
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerUp);
    };
  }, [dragSessionActive, finishDrag, handlePointerMove]);

  return (
  <Box as="section" aria-label="Top content">
    <HStack align="flex-start" justify="space-between" spacing={3} mb="10px">
      <VStack align="flex-start" spacing={0}>
        <Text as="h3" textStyle="eyebrow" color={ff.text2} m={0}>
          Top Content
        </Text>
        <Text mt="4px" fontSize="14px" lineHeight="1.4" color={ff.body}>
          Showcase your best work.
          {canReorder ? (
            <>
              {' '}
              Use the drag handle to reorder; the first slot is your marketplace cover.
            </>
          ) : null}
        </Text>
      </VStack>
      <Text
        fontSize="12px"
        lineHeight="17px"
        fontWeight={500}
        color={ff.text2}
        flexShrink={0}
        pt="1px"
      >
        {filledCount} / {MAX_PORTFOLIO} (min. {MIN_PORTFOLIO})
      </Text>
    </HStack>

    <Box
      w="100%"
      mb="12px"
      overflowX={isDragging ? 'visible' : 'auto'}
      overflowY={isDragging ? 'visible' : 'hidden'}
      sx={{
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'stable',
        scrollSnapType: isDragging ? 'none' : { base: 'x mandatory', md: 'none' },
      }}
    >
      <HStack
        spacing="10px"
        align="stretch"
        w="max-content"
        minH="min-content"
        pr="2px"
        pb="4px"
      >
        {displaySlots.map((src, i) => {
          const isDraggedItem = dragSessionActive && src?.id === draggedId;
          const isDraggableSlot = Boolean(src && canReorder && !dragSessionActive);

          return (
          <Box
            key={src?.id ?? `empty-${i}`}
            ref={(el) => {
              slotRefs.current[i] = el;
            }}
            position="relative"
            flexShrink={0}
            w="calc((100vw - 80px) / 2.4)"
            maxW="200px"
            borderRadius="20px"
            border="1px dashed"
            borderColor={isDraggedItem ? ff.ink : src ? ff.line : ff.border}
            borderStyle={isDraggedItem || src ? 'solid' : 'dashed'}
            borderWidth={isDraggedItem ? '2px' : '1.5px'}
            bg={isDraggedItem ? ff.limeSurface : ff.grey}
            overflow="hidden"
            cursor="pointer"
            opacity={isDraggedItem ? 0.4 : 1}
            transition={isDragging ? 'none' : 'border-color 0.15s, opacity 0.2s'}
            sx={{
              aspectRatio: '126 / 224',
              scrollSnapAlign: { base: 'start', md: 'unset' },
            }}
            onClick={() => onSlotClick(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSlotClick(i);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Portfolio slot ${i + 1} of ${displaySlots.length}${
              src ? (i === 0 ? ', cover image' : '') : ', add media'
            }`}
          >
            {i === 0 && src && !isDraggedItem ? (
              <Tooltip
                label="This is your cover image — it can be a photo or video, and it's the first thing brands see when browsing the marketplace."
                placement="bottom-start"
                hasArrow
                fontSize="12px"
                maxW="200px"
                borderRadius="12px"
                bg={ff.ink}
                px={3}
                py={2}
                isOpen={coverHintOpen}
                onClose={() => setCoverHintOpen(false)}
              >
                <Box
                  position="absolute"
                  top="6px"
                  left="6px"
                  zIndex={2}
                  bg={coverHintOpen ? 'rgba(0,0,0,0.80)' : 'rgba(0,0,0,0.55)'}
                  backdropFilter="blur(4px)"
                  borderRadius="full"
                  px="8px"
                  py="3px"
                  display="flex"
                  alignItems="center"
                  gap="3px"
                  cursor="pointer"
                  border="1px solid"
                  borderColor={coverHintOpen ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)'}
                  transition="background 0.15s, border-color 0.15s"
                  _active={{ bg: 'rgba(0,0,0,0.90)' }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCoverHintOpen((v) => !v);
                  }}
                  onMouseEnter={() => setCoverHintOpen(true)}
                  onMouseLeave={() => setCoverHintOpen(false)}
                >
                  <Text textStyle="eyebrow" fontSize="9px" color="white">
                    Cover image
                  </Text>
                  <Text fontSize="8.5px" fontWeight={400} color="whiteAlpha.700" lineHeight={1}>
                    ⓘ
                  </Text>
                </Box>
              </Tooltip>
            ) : null}
            {src && !isDraggedItem ? (
              <>
                {renderSlotMedia(src)}
                {(src.associatedBrand || src.caption) ? (
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    px="8px"
                    py="6px"
                    bg="rgba(0,0,0,0.5)"
                    pointerEvents="none"
                  >
                    {src.associatedBrand ? (
                      <Text
                        fontSize="11px"
                        lineHeight="1.2"
                        fontWeight={600}
                        color="white"
                        noOfLines={1}
                      >
                        {src.associatedBrand}
                      </Text>
                    ) : null}
                    {src.caption ? (
                      <Text
                        fontSize="9px"
                        lineHeight="1.3"
                        fontWeight={400}
                        color="whiteAlpha.800"
                        noOfLines={2}
                        mt={src.associatedBrand ? '1px' : 0}
                      >
                        {src.caption}
                      </Text>
                    ) : null}
                  </Box>
                ) : null}
                {isDraggableSlot ? (
                  <Flex
                    position="absolute"
                    top="6px"
                    right="6px"
                    zIndex={3}
                    w="32px"
                    h="32px"
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg="rgba(0,0,0,0.55)"
                    backdropFilter="blur(4px)"
                    color="white"
                    cursor="grab"
                    userSelect="none"
                    sx={{ touchAction: 'none' }}
                    aria-label={`Drag to reorder slot ${i + 1}`}
                    onPointerDown={(e) => handleDragHandlePointerDown(i, e)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box pointerEvents="none" transform="scale(0.9)">
                      <MdDragIndicator size={18} />
                    </Box>
                  </Flex>
                ) : null}
              </>
            ) : src && isDraggedItem ? (
              <Flex align="center" justify="center" h="100%" pointerEvents="none">
                <Text fontSize="12px" color={ff.ink} fontWeight={600}>
                  Drop here
                </Text>
              </Flex>
            ) : (
              <Flex
                align="center"
                justify="center"
                h="100%"
                minH="100%"
                direction="column"
                pointerEvents="none"
              >
                <UploadSlotPlaceholderIcon />
              </Flex>
            )}
          </Box>
          );
        })}
      </HStack>
    </Box>

    {draggedEntry && ghostPosition && ghostMetrics && dragSessionActive ? (
      <Box
        position="fixed"
        left={`${ghostPosition.left}px`}
        top={`${ghostPosition.top}px`}
        w={`${ghostMetrics.width}px`}
        h={`${ghostMetrics.height}px`}
        zIndex={2000}
        borderRadius="20px"
        overflow="hidden"
        border="2px solid"
        borderColor={ff.ink}
        boxShadow="0 16px 40px rgba(15, 23, 42, 0.28)"
        pointerEvents="none"
      >
        {renderSlotMedia(draggedEntry)}
        {(draggedEntry.associatedBrand || draggedEntry.caption) ? (
          <Box
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            px="6px"
            py="4px"
            bg="rgba(0,0,0,0.5)"
          >
            {draggedEntry.associatedBrand ? (
              <Text fontSize="10px" lineHeight="1.2" fontWeight={600} color="white" noOfLines={1}>
                {draggedEntry.associatedBrand}
              </Text>
            ) : null}
            {draggedEntry.caption ? (
              <Text
                fontSize="9px"
                lineHeight="1.3"
                fontWeight={400}
                color="whiteAlpha.800"
                noOfLines={2}
                mt={draggedEntry.associatedBrand ? '1px' : 0}
              >
                {draggedEntry.caption}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Box>
    ) : null}
  </Box>
  );
};

export default TopVideosSection;
